<script setup lang="ts">
import { computed } from 'vue';
import { useDataStore } from '@/stores/useDataStore';
import { useHistorycalStore } from '@/stores/useHistorycalStore';
import { useI18n } from 'vue-i18n';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  LineController,
  ScatterController
} from 'chart.js';
import { Chart } from 'vue-chartjs';
import { metricOptions, getAbsorbanceData, getMetricAggregatedData } from '@/functions/historical_data';

// Register Chart.js plugins
ChartJS.register(Title, Tooltip, Legend, LineController, LineElement, PointElement, LinearScale, CategoryScale, ScatterController);

const dataStore = useDataStore();
const historyStore = useHistorycalStore();
const { t } = useI18n();

const options = metricOptions(t);

// Couleurs pour chaque métrique
const metricColors: Record<string, { border: string; background: string }> = {
  temperature: { border: 'rgb(255, 99, 132)', background: 'rgb(255, 99, 132)' }, // Rouge
  ph: { border: 'rgb(54, 162, 235)', background: 'rgb(54, 162, 235)' }, // Bleu
};

// Processed chart data
const processedChart: any = computed(() => {
  const startdaycode = dataStore.selectedCulture?.startdaycode?.startday_code || '';
  const prelevements = dataStore.selectedCulture?.prelevements || [];
  const volumeUtilePBR = dataStore.selectedCulture?.pbr.volume;
  const historicalData = historyStore.historicalData?.results || [];

  const absorbanceData = getAbsorbanceData(startdaycode, prelevements, volumeUtilePBR);

  // Générer les datasets pour chaque métrique sélectionnée (moyenne + range)
  const metricDatasets = historyStore.selectedMetrics.flatMap(metric => {
    const { averageData, rangeData } = getMetricAggregatedData(
      metric,
      historicalData,
      startdaycode,
      true, // Inclure range
      false // Exclure outliers
    );

    return [
      {
        label: `${options.find(opt => opt.value === metric)?.label || metric} (Moyenne)`,
        data: averageData,
        borderColor: metricColors[metric].border,
        backgroundColor: metricColors[metric].background,
        fill: false,
        tension: 0.3,
        pointRadius: 6,
        pointHoverRadius: 7,
        pointBorderWidth: 2,
        pointBackgroundColor: metricColors[metric].background,
        pointBorderColor: metricColors[metric].border,
        yAxisID: 'metric',
      },
      {
        label: `${options.find(opt => opt.value === metric)?.label || metric} (Range)`,
        data: rangeData,
        borderColor: `${metricColors[metric].border.replace('rgb', 'rgba').replace(')', ', 0.6)')}`,
        backgroundColor: `${metricColors[metric].background.replace('rgb', 'rgba').replace(')', ', 0.6)')}`,
        pointRadius: 3,
        pointHoverRadius: 4,
        pointBackgroundColor: (ctx: any) =>
          ctx.raw?.isMin
            ? `${metricColors[metric].background.replace('rgb', 'rgba').replace(')', ', 0.5)')}`
            : `${metricColors[metric].background.replace('rgb', 'rgba').replace(')', ', 0.8)')}`,
        pointBorderColor: (ctx: any) =>
          ctx.raw?.isMin
            ? `${metricColors[metric].border.replace('rgb', 'rgba').replace(')', ', 0.5)')}`
            : `${metricColors[metric].border.replace('rgb', 'rgba').replace(')', ', 0.8)')}`,
        pointBorderWidth: 1,
        showLine: true,
        tension: 0,
        segment: {
          borderColor: (ctx: any) => {
            const p0 = ctx.p0.raw as ChartPoint;
            const p1 = ctx.p1.raw as ChartPoint;
            return p0.x === p1.x
              ? `${metricColors[metric].border.replace('rgb', 'rgba').replace(')', ', 0.6)')}`
              : 'transparent';
          },
        },
        yAxisID: 'metric',
      },
    ];
  });

  const chartData = {
    datasets: [
      {
        label: 'Absorbance',
        data: absorbanceData,
        borderColor: 'green',
        backgroundColor: 'green',
        pointRadius: 5,
        pointHoverRadius: 6,
        pointBackgroundColor: 'green',
        pointBorderColor: 'green',
        pointBorderWidth: 1,
        yAxisID: 'absorbance',
      },
      ...metricDatasets,
    ],
  };

  return { chartData, ignoredMetrics: metricDatasets.length ? processedChart.value?.ignoredMetrics || [] : [] };
});

// Chart options
const chartOptions = computed(() => {
  const defaultDatasets = [{ data: [] }, ...historyStore.selectedMetrics.flatMap(() => [{ data: [] }, { data: [] }])];
  const datasets = processedChart.value?.chartData?.datasets || defaultDatasets;

  const absorbanceValues = datasets[0].data.map((p: ChartPoint) => p.y).filter((val: any) => Number.isFinite(val)) || [];
  const metricValues = datasets
    .slice(1)
    .flatMap((dataset: any) => dataset.data.map((p: ChartPoint) => p.y))
    .filter((val: any) => Number.isFinite(val)) || [];

  const getMin = (arr: number[]) => (arr.length ? Math.min(...arr) : 0);
  const getMax = (arr: number[]) => (arr.length ? Math.max(...arr) : 1);

  const minAbsorbance = absorbanceValues.length
    ? parseFloat((getMin(absorbanceValues) - (getMax(absorbanceValues) - getMin(absorbanceValues)) * 0.1).toFixed(2))
    : 0;
  const maxAbsorbance = absorbanceValues.length
    ? parseFloat((getMax(absorbanceValues) + (getMax(absorbanceValues) - getMin(absorbanceValues)) * 0.1).toFixed(2))
    : 1;
  const minMetric = metricValues.length
    ? parseFloat((getMin(metricValues) - (getMax(metricValues) - getMin(metricValues)) * 0.1).toFixed(2))
    : 0;
  const maxMetric = metricValues.length
    ? parseFloat((getMax(metricValues) + (getMax(metricValues) - getMin(metricValues)) * 0.1).toFixed(2))
    : 100;

  return {
    responsive: true,
    plugins: {
      tooltip: {
        callbacks: {
          title: (tooltipItems: any) => {
            const item = tooltipItems[0];
            return `Âge : ${item.parsed.x} jours`;
          },
          label: (tooltipItem: any) => {
            const datasetLabel = tooltipItem.dataset.label;
            const yValue = tooltipItem.parsed.y;
            const point = tooltipItem.raw as MetricPoint | ChartPoint;
            const metricMatch = datasetLabel.match(/^(.*?)\s*\((Moyenne|Range)\)$/);
            const metric = metricMatch ? metricMatch[1] : datasetLabel;
            const metricKey = options.find(opt => opt.label === metric)?.value || '';

            if (datasetLabel === 'Absorbance') {
              return `Absorbance : ${yValue}`;
            }

            let displayValue: string | number = yValue;
            const unit = metricKey === 'temperature' ? 'C°' : '';

            if (datasetLabel.includes('Moyenne') && 'min' in point && 'max' in point && 'avg' in point) {
              return [
                `${datasetLabel} : ${displayValue} ${unit}`,
                `Min : ${point.min} ${unit}`,
                `Max : ${point.max} ${unit}`,
              ];
            }
            if (datasetLabel.includes('Range') && 'min' in point && 'max' in point && 'avg' in point) {
              return [
                `${point.isMin ? 'Min' : 'Max'} : ${displayValue} ${unit}`,
                `Moyenne : ${point.avg} ${unit}`,
                `Min : ${point.min} ${unit}`,
                `Max : ${point.max} ${unit}`,
              ];
            }
            return `${datasetLabel} : ${displayValue} ${unit}`;
          },
        },
      },
      legend: {
        position: 'top' as const,
        labels: {
          generateLabels: () => [
            {
              text: 'Absorbance',
              fillStyle: 'green',
              strokeStyle: 'green',
              lineWidth: 3,
            },
            ...historyStore.selectedMetrics.flatMap(metric => [
              {
                text: `${options.find(opt => opt.value === metric)?.label || metric} (Moyenne)`,
                fillStyle: metricColors[metric].background,
                strokeStyle: metricColors[metric].border,
                lineWidth: 3,
              },
              {
                text: `${options.find(opt => opt.value === metric)?.label || metric} (Range)`,
                fillStyle: metricColors[metric].background.replace('rgb', 'rgba').replace(')', ', 0.6)'),
                strokeStyle: metricColors[metric].border.replace('rgb', 'rgba').replace(')', ', 0.6)'),
                lineWidth: 3,
              },
            ]),
          ],
        },
      },
    },
    scales: {
      absorbance: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Absorbance',
        },
        min: minAbsorbance,
        max: maxAbsorbance,
        position: 'right' as const,
      },
      metric: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Métriques',
        },
        min: minMetric,
        max: maxMetric,
        position: 'left' as const,
      },
      x: {
        type: 'linear' as const,
        title: {
          display: true,
          text: 'Âge (jours)',
        },
        ticks: {
          stepSize: 1,
        },
      },
    },
  };
});
</script>

<template>
  <div v-if="dataStore.selectedCulture?.pbr?.numero_pbr?.startsWith('B')">
    <div v-if="historyStore.historicalData && dataStore.selectedCulture" class="chart-wrapper">
      <!-- Multi-select avec checkboxes côte à côte -->
      <div class="metric-selector mb-4">
        <label class="selector-label"></label>
        <div class="checkbox-group">
          <div v-for="option in options" class="checkbox-item" :key="option.value">
            <input :id="`metric-${option.value}`" v-model="historyStore.selectedMetrics" class="styled-checkbox"
              type="checkbox" :value="option.value" />
            <label :for="`metric-${option.value}`">{{ option.label }}</label>
          </div>
        </div>
      </div>
      <div v-if="processedChart.chartData.datasets[1]?.data.length === 0" class="no-data">
        {{ $t('noHistoricalData') }} {{ dataStore.selectedCulture.pbr.numero_pbr }}
      </div>
      <div v-else class="w-full max-w-3xl mx-auto">
        <Chart :type="'line'" :data="processedChart.chartData" :options="chartOptions" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.chart-wrapper {
  padding: 20px;
}

.metric-selector {
  max-width: 600px;
  margin: 0 auto;
}

.selector-label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
}

.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.checkbox-item {
  display: flex;
  gap: 8px;
  padding-bottom: 15px;
}

.checkbox-item label {
  cursor: pointer;
}

.no-data {
  text-align: center;
  color: red;
  font-weight: bold;
}
</style>